import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
    LayoutDashboard, Calendar, Star, DollarSign,
    ShoppingBag, Mail, MessageSquare, Users,
    LogOut, Plus, Trash2, Edit2, Menu, X, RefreshCw, PlusCircle,
    Eye, EyeOff, Settings, Store, Image as ImageIcon, Upload, FileText, Database, Repeat, Megaphone, Pencil
} from "lucide-react";
import { DataTransferTab } from "../components/admin/DataTransferTab";
import { PostsTab } from "../components/admin/PostsTab";
import { EditDonationModal, confirmDeleteDonation } from "../components/admin/EditDonationModal";
import { SPONSOR_LEVELS, FEATURED_LEVEL_IDS, LevelBadge } from "../components/SponsorLevels";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SESSION_KEY = "slsa_admin_key";

// ─── Login ───────────────────────────────────────────────────────────────────
const AdminLogin = ({ onLogin }) => {
    const [pw, setPw] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true); setError(false);
        try {
            await axios.get(`${API}/admin/stats`, { headers: { "x-admin-password": pw } });
            sessionStorage.setItem(SESSION_KEY, pw);
            onLogin(pw);
        } catch { setError(true); }
        finally { setLoading(false); }
    };

    return (
        <div className="min-h-screen bg-[#3D2008] flex items-center justify-center p-4">
            <div className="w-full max-w-sm">
                <div className="text-center mb-8">
                    <div className="font-anton text-4xl text-[#F5EDD6] uppercase tracking-wide">SLSA</div>
                    <div className="font-manrope text-[#C9A84C] text-lg">Admin Panel</div>
                </div>
                <form onSubmit={submit} className="bg-[#F5EDD6] border-2 border-[#C9A84C] p-8 shadow-[8px_8px_0px_0px_rgba(201,168,76,0.4)]">
                    <label className="font-manrope text-xs font-bold text-[#3D2008] uppercase tracking-wide block mb-2">Password</label>
                    <input
                        type="password"
                        placeholder="Enter admin password"
                        value={pw}
                        onChange={e => { setPw(e.target.value); setError(false); }}
                        autoFocus
                        data-testid="admin-password-input"
                        className={`w-full h-12 px-4 bg-white border-2 font-manrope text-[#3D2008] mb-3 focus:outline-none transition-colors ${error ? "border-[#C4522A]" : "border-[#3D2008] focus:border-[#C4522A]"}`}
                    />
                    {error && <p className="font-manrope text-sm text-[#C4522A] mb-3" data-testid="admin-login-error">Wrong password. Try again.</p>}
                    <button type="submit" disabled={loading} data-testid="admin-login-btn"
                        className="w-full py-3 bg-[#C4522A] text-white font-anton uppercase tracking-wider border-2 border-[#3D2008] shadow-[4px_4px_0px_0px_rgba(61,32,8,0.3)] hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-none transition-transform disabled:opacity-60">
                        {loading ? "Checking..." : "Enter"}
                    </button>
                </form>
            </div>
        </div>
    );
};

// ─── Record Donation Tab ─────────────────────────────────────────────────────
const METHODS = [
    { value: "cash",         label: "Cash" },
    { value: "check",        label: "Check" },
    { value: "stripe",       label: "Online / Stripe" },
    { value: "merchandise",  label: "Merchandise Sale" },
    { value: "other",        label: "Other" },
];
const METHOD_BADGE = {
    cash: "bg-[#2D5A27] text-white",
    check: "bg-[#4A7FA5] text-white",
    stripe: "bg-[#C4522A] text-white",
    merchandise: "bg-[#C9A84C] text-[#3D2008]",
    other: "bg-gray-400 text-white",
};

const RecordDonationTab = ({ adminKey, onRefresh }) => {
    const h = { "x-admin-password": adminKey };
    const [form, setForm] = useState({ donor_name: "", donor_email: "", amount: "", method: "cash", date_received: new Date().toISOString().split("T")[0], note: "" });
    const [donorNames, setDonorNames] = useState([]);
    useEffect(() => {
        axios.get(`${API}/admin/donor-names`, { headers: { "x-admin-password": adminKey } }).then(r => setDonorNames(r.data)).catch(() => {});
    }, [adminKey]);
    const [saving, setSaving] = useState(false);
    const [recent, setRecent] = useState([]);
    const f = k => e => setForm(p => ({ ...p, [k]: e.target.value }));

    const loadRecent = useCallback(async () => {
        try {
            const r = await axios.get(`${API}/admin/donations`, { headers: { "x-admin-password": adminKey } });
            setRecent(r.data.filter(d => d.session_id?.startsWith("manual_")).slice(0, 10));
        } catch { setRecent([]); }
    }, [adminKey]);

    useEffect(() => { loadRecent(); }, [loadRecent]);

    const submit = async e => {
        e.preventDefault();
        if (!form.amount || parseFloat(form.amount) <= 0) { toast.error("Enter a valid amount."); return; }
        setSaving(true);
        try {
            const res = await axios.post(`${API}/admin/record-donation`, {
                ...form, amount: parseFloat(form.amount),
            }, { headers: h });
            toast.success(res.data.message);
            setForm(p => ({ ...p, donor_name: "", donor_email: "", amount: "", note: "" }));
            loadRecent();
            if (onRefresh) onRefresh();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to record donation.");
        } finally { setSaving(false); }
    };

    const del = async (sid) => {
        if (!window.confirm("Remove this manually recorded donation?")) return;
        await axios.delete(`${API}/admin/donations/${sid}`, { headers: h });
        toast.success("Removed."); loadRecent();
        if (onRefresh) onRefresh();
    };

    return (
        <div data-testid="record-donation-tab">
            <h2 className="font-anton text-2xl text-[#3D2008] uppercase mb-2">Record a Donation</h2>
            <p className="font-manrope text-sm text-[#5C4A35] mb-6 max-w-lg">
                Log cash, check, or external online donations. They'll be added to the fundraising total and donor count on the public site immediately.
            </p>

            <form onSubmit={submit} className="bg-[#F5EDD6] border-2 border-[#3D2008] p-6 mb-8 max-w-2xl">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                        <label className={labelCls}>Donor Name <span className="font-normal normal-case text-gray-400">(optional)</span></label>
                        <input type="text" list="donor-names" value={form.donor_name} autoComplete="off"
                            onChange={e => {
                                const v = e.target.value;
                                const match = donorNames.find(d => d.name.toLowerCase() === v.trim().toLowerCase());
                                setForm(p => ({ ...p, donor_name: v, donor_email: match?.email && !p.donor_email ? match.email : p.donor_email }));
                            }}
                            placeholder="Jane Smith" data-testid="record-donor-name" className={inputCls} />
                        <datalist id="donor-names" data-testid="donor-names-list">
                            {donorNames.map(d => <option key={d.name} value={d.name}>{d.email}</option>)}
                        </datalist>
                    </div>
                    <div>
                        <label className={labelCls}>Donor Email <span className="font-normal normal-case text-gray-400">(optional)</span></label>
                        <input type="email" value={form.donor_email} onChange={f("donor_email")} placeholder="jane@example.com" className={inputCls} />
                    </div>
                    <div>
                        <label className={labelCls}>Amount ($) *</label>
                        <input type="number" min="0.01" step="0.01" value={form.amount} onChange={f("amount")} placeholder="50.00" required data-testid="record-amount" className={inputCls} />
                    </div>
                    <div>
                        <label className={labelCls}>Payment Method *</label>
                        <select value={form.method} onChange={f("method")} data-testid="record-method" className={inputCls}>
                            {METHODS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className={labelCls}>Date Received</label>
                        <input type="date" value={form.date_received} onChange={f("date_received")} className={inputCls} />
                    </div>
                    <div>
                        <label className={labelCls}>Internal Note <span className="font-normal normal-case text-gray-400">(optional)</span></label>
                        <input type="text" value={form.note} onChange={f("note")} placeholder="e.g. Received at July 4th event" className={inputCls} />
                    </div>
                </div>
                <button type="submit" disabled={saving} data-testid="record-donation-submit"
                    className="mt-5 flex items-center gap-2 px-6 py-2.5 bg-[#2D5A27] text-white font-manrope font-semibold text-sm border-2 border-[#3D2008] shadow-[3px_3px_0px_0px_rgba(61,32,8,0.3)] hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-none transition-transform disabled:opacity-60">
                    <PlusCircle size={15} /> {saving ? "Recording..." : "Record Donation"}
                </button>
            </form>

            {recent.length > 0 && (
                <div>
                    <h3 className="font-anton text-lg text-[#3D2008] uppercase mb-3">Recently Recorded (Manual)</h3>
                    <div className="overflow-x-auto border-2 border-[#3D2008]">
                        <table className="w-full border-collapse text-sm">
                            <thead><tr className="bg-[#3D2008] text-[#F5EDD6]">
                                {["Date", "Name", "Email", "Amount", "Method", "Note", ""].map(h => (
                                    <th key={h} className="text-left px-4 py-3 font-manrope font-bold uppercase text-xs tracking-wide whitespace-nowrap">{h}</th>
                                ))}
                            </tr></thead>
                            <tbody>
                                {recent.map((d, i) => (
                                    <tr key={d.session_id} className={i % 2 === 0 ? "bg-white" : "bg-[#F5EDD6]/60"}>
                                        <td className="px-4 py-3 font-manrope text-[#3D2008] border-b border-gray-100">{d.created_at ? new Date(d.created_at).toLocaleDateString() : "—"}</td>
                                        <td className="px-4 py-3 font-manrope text-[#3D2008] border-b border-gray-100">{d.donor_name || "—"}</td>
                                        <td className="px-4 py-3 font-manrope text-[#3D2008] border-b border-gray-100">{d.donor_email || "—"}</td>
                                        <td className="px-4 py-3 font-manrope text-[#3D2008] border-b border-gray-100 font-semibold">${d.amount}</td>
                                        <td className="px-4 py-3 border-b border-gray-100">
                                            <span className={`text-xs font-bold px-2 py-0.5 uppercase ${METHOD_BADGE[d.method] || "bg-gray-300 text-white"}`}>{d.method || "—"}</span>
                                        </td>
                                        <td className="px-4 py-3 border-b border-gray-100 align-top"><ExpandableText text={d.note} testId={`donation-note-${i}`} /></td>
                                        <td className="px-4 py-3 border-b border-gray-100">
                                            <button onClick={() => del(d.session_id)} className="p-1.5 border border-[#C4522A] text-[#C4522A] hover:bg-[#C4522A] hover:text-white transition-colors">
                                                <Trash2 size={12} />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
};

// ─── Shared: stat card ────────────────────────────────────────────────────────
const StatCard = ({ label, value, color }) => (
    <div className={`${color} p-5 text-white`} data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
        <div className="font-anton text-3xl mb-1">{value}</div>
        <div className="font-manrope text-xs text-white/70 uppercase tracking-wide">{label}</div>
    </div>
);

// ─── Shared: click-to-expand long text (messages, notes) ─────────────────────
const ExpandableText = ({ text, testId }) => {
    const [open, setOpen] = useState(false);
    if (!text) return <span className="text-gray-400">—</span>;
    return (
        <button type="button" onClick={() => setOpen(o => !o)} data-testid={testId}
            title={open ? "Click to collapse" : "Click to read the whole thing"}
            className={`text-left font-manrope text-[#5C4A35] hover:text-[#3D2008] transition-colors ${open ? "whitespace-pre-wrap break-words max-w-md" : "block truncate max-w-xs"}`}>
            {text}
            {open && <span className="block mt-1 text-[11px] font-bold uppercase tracking-wide text-[#C4522A]">▲ Show less</span>}
        </button>
    );
};

// ─── Shared: data table ───────────────────────────────────────────────────────
const DataTable = ({ headers, rows, empty }) => (
    <div className="overflow-x-auto border-2 border-[#3D2008]">
        {rows.length === 0
            ? <p className="text-center py-12 text-gray-400 font-manrope">{empty}</p>
            : <table className="w-full border-collapse text-sm">
                <thead>
                    <tr className="bg-[#3D2008] text-[#F5EDD6]">
                        {headers.map(h => <th key={h} className="text-left px-4 py-3 font-manrope font-bold uppercase text-xs tracking-wide whitespace-nowrap">{h}</th>)}
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row, i) => (
                        <tr key={i} className={i % 2 === 0 ? "bg-white" : "bg-[#F5EDD6]/60"}>
                            {row.map((cell, j) => <td key={j} className="px-4 py-3 font-manrope text-[#3D2008] border-b border-gray-100 max-w-xs">{cell}</td>)}
                        </tr>
                    ))}
                </tbody>
            </table>
        }
    </div>
);

const inputCls = "w-full px-3 py-2 border-2 border-[#3D2008] bg-white font-manrope text-sm text-[#3D2008] focus:outline-none focus:border-[#C4522A] transition-colors";
const labelCls = "font-manrope text-xs font-bold text-[#3D2008] uppercase tracking-wide block mb-1";

const srcUrl = (u) => (u || "").startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u;

// Reusable upload button — POSTs a file to /api/admin/upload and returns a servable URL.
const UploadButton = ({ adminKey, accept = "image/*", label = "Upload", onUploaded }) => {
    const ref = useRef(null);
    const [busy, setBusy] = useState(false);
    const pick = async (e) => {
        const file = e.target.files && e.target.files[0];
        if (!file) return;
        setBusy(true);
        const fd = new FormData(); fd.append("file", file);
        try {
            const r = await axios.post(`${API}/admin/upload`, fd, { headers: { "x-admin-password": adminKey, "Content-Type": "multipart/form-data" } });
            onUploaded(r.data.url);
            toast.success("Uploaded!");
        } catch { toast.error("Upload failed."); }
        finally { setBusy(false); if (ref.current) ref.current.value = ""; }
    };
    return (
        <>
            <button type="button" onClick={() => ref.current && ref.current.click()} disabled={busy} data-testid="upload-btn"
                className="flex items-center gap-1.5 whitespace-nowrap px-3 py-2 bg-[#2D5A27] text-white font-manrope font-semibold text-xs border-2 border-[#3D2008] disabled:opacity-60">
                <Upload size={13} /> {busy ? "Uploading…" : label}
            </button>
            <input ref={ref} type="file" accept={accept} onChange={pick} className="hidden" />
        </>
    );
};

// ─── Events Tab ──────────────────────────────────────────────────────────────
const EventsTab = ({ adminKey }) => {
    const [events, setEvents] = useState([]);
    const [showForm, setShowForm] = useState(false);
    const [editId, setEditId] = useState(null);
    const [form, setForm] = useState({ title: "", description: "", date: "", date_note: "", location: "", image_url: "" });
    const [saving, setSaving] = useState(false);
    const h = { "x-admin-password": adminKey };
    const f = (k) => (e) => setForm(p => ({ ...p, [k]: e.target.value }));

    const load = useCallback(async () => {
        const r = await axios.get(`${API}/events`);
        setEvents(r.data);
    }, []);

    useEffect(() => { load(); }, [load]);

    const resetForm = () => { setForm({ title: "", description: "", date: "", date_note: "", location: "", image_url: "" }); setEditId(null); setShowForm(false); };

    const submit = async (e) => {
        e.preventDefault();
        if (!form.title || !form.description || !form.date) { toast.error("Title, description and date are required."); return; }
        setSaving(true);
        try {
            if (editId) {
                await axios.put(`${API}/admin/events/${editId}`, form, { headers: h });
                toast.success("Event updated!");
            } else {
                await axios.post(`${API}/admin/events`, form, { headers: h });
                toast.success("Event added!");
            }
            resetForm(); load();
        } catch { toast.error("Failed to save."); }
        finally { setSaving(false); }
    };

    const del = async (id) => {
        if (!window.confirm("Delete this event?")) return;
        await axios.delete(`${API}/admin/events/${id}`, { headers: h });
        toast.success("Deleted."); load();
    };

    const edit = (ev) => {
        setForm({ title: ev.title || "", description: ev.description || "", date: ev.date || "", date_note: ev.date_note || "", location: ev.location || "", image_url: ev.image_url || "" });
        setEditId(ev.id); setShowForm(true);
    };

    return (
        <div data-testid="events-tab">
            <div className="flex items-center justify-between mb-6">
                <h2 className="font-anton text-2xl text-[#3D2008] uppercase">Events ({events.length})</h2>
                <button onClick={() => { setShowForm(!showForm); setEditId(null); setForm({ title: "", description: "", date: "", date_note: "", location: "", image_url: "" }); }}
                    data-testid="add-event-btn"
                    className="flex items-center gap-2 px-4 py-2 bg-[#C4522A] text-white font-manrope font-semibold text-sm border-2 border-[#3D2008] shadow-[3px_3px_0px_0px_rgba(61,32,8,0.3)] hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-none transition-transform">
                    <Plus size={15} /> Add Event
                </button>
            </div>

            {showForm && (
                <form onSubmit={submit} className="bg-[#F5EDD6] border-2 border-[#3D2008] p-6 mb-6" data-testid="event-form">
                    <h3 className="font-anton text-lg text-[#3D2008] uppercase mb-4">{editId ? "Edit Event" : "New Event"}</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="sm:col-span-2"><label className={labelCls}>Title *</label><input type="text" value={form.title} onChange={f("title")} required placeholder="Event title" data-testid="event-title-input" className={inputCls} /></div>
                        <div className="sm:col-span-2"><label className={labelCls}>Description *</label><textarea value={form.description} onChange={f("description")} required rows={3} placeholder="What's happening..." className={`${inputCls} resize-none`} /></div>
                        <div><label className={labelCls}>Date *</label><input type="date" value={form.date} onChange={f("date")} required className={inputCls} /></div>
                        <div><label className={labelCls}>Date Note <span className="font-normal normal-case text-gray-400">(e.g. "Date TBD")</span></label><input type="text" value={form.date_note} onChange={f("date_note")} placeholder="All of May 2026" className={inputCls} /></div>
                        <div className="sm:col-span-2"><label className={labelCls}>Location</label><input type="text" value={form.location} onChange={f("location")} placeholder="Seeley Lake, Montana" className={inputCls} /></div>
                        <div className="sm:col-span-2">
                            <label className={labelCls}>Event Image / Flyer</label>
                            <div className="flex items-center gap-3">
                                <input type="text" value={form.image_url} onChange={f("image_url")} placeholder="Upload or paste image URL" className={inputCls} />
                                <UploadButton adminKey={adminKey} accept="image/*" onUploaded={(url) => setForm(p => ({ ...p, image_url: url }))} />
                            </div>
                            {form.image_url && <img src={srcUrl(form.image_url)} alt="" className="mt-2 h-24 object-cover border-2 border-[#3D2008]" />}
                        </div>
                    </div>
                    <div className="flex gap-3 mt-5">
                        <button type="submit" disabled={saving} data-testid="event-save-btn" className="px-6 py-2 bg-[#C4522A] text-white font-manrope font-semibold text-sm border-2 border-[#3D2008] disabled:opacity-60">{saving ? "Saving..." : editId ? "Update" : "Add Event"}</button>
                        <button type="button" onClick={resetForm} className="px-6 py-2 bg-white text-[#3D2008] font-manrope font-semibold text-sm border-2 border-[#3D2008]">Cancel</button>
                    </div>
                </form>
            )}

            <div className="space-y-2">
                {events.map(ev => (
                    <div key={ev.id} className="bg-white border-2 border-[#3D2008] p-4 flex gap-4 items-start" data-testid={`event-row-${ev.id}`}>
                        <div className="flex-1 min-w-0">
                            <p className="font-anton text-[#3D2008] uppercase">{ev.title}</p>
                            <p className="font-manrope text-xs text-[#C4522A] font-semibold">{ev.date_note || ev.date} {ev.location && `· ${ev.location}`}</p>
                            <p className="font-manrope text-sm text-[#5C4A35] mt-1 line-clamp-2">{ev.description}</p>
                        </div>
                        <div className="flex gap-2 flex-shrink-0">
                            <button onClick={() => edit(ev)} className="p-2 border border-[#4A7FA5] text-[#4A7FA5] hover:bg-[#4A7FA5] hover:text-white transition-colors"><Edit2 size={13} /></button>
                            <button onClick={() => del(ev.id)} className="p-2 border border-[#C4522A] text-[#C4522A] hover:bg-[#C4522A] hover:text-white transition-colors"><Trash2 size={13} /></button>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

// ─── Sponsors Tab ─────────────────────────────────────────────────────────────
const TIER_OPTS = SPONSOR_LEVELS.map(l => ({ v: l.id, l: l.label + (FEATURED_LEVEL_IDS.includes(l.id) ? " (featured card)" : "") }));
const EMPTY_SPONSOR = { name: "", tier: "big-gus", description: "", website: "", logo_url: "", hidden: false };

const SponsorsTab = ({ adminKey }) => {
    const [sponsors, setSponsors] = useState([]);
    const [showForm, setShowForm] = useState(false);
    const [editId, setEditId] = useState(null);
    const [form, setForm] = useState(EMPTY_SPONSOR);
    const [saving, setSaving] = useState(false);
    const h = { "x-admin-password": adminKey };
    const f = (k) => (e) => setForm(p => ({ ...p, [k]: e.target.value }));

    const load = useCallback(async () => {
        const r = await axios.get(`${API}/admin/sponsors`, { headers: { "x-admin-password": adminKey } });
        setSponsors(r.data);
    }, [adminKey]);
    useEffect(() => { load(); }, [load]);

    const reset = () => { setForm(EMPTY_SPONSOR); setEditId(null); setShowForm(false); };

    const [thanking, setThanking] = useState("");
    const postThankYou = async (s) => {
        setThanking(s.id);
        try {
            await axios.post(`${API}/admin/sponsors/${s.id}/thank-you-post`, {}, { headers: h });
            toast.success(`Thank-you to ${s.name} is live on What's Happening!`, {
                action: { label: "View", onClick: () => window.open("/whats-happening", "_blank") },
            });
            load();
        } catch (e) { toast.error(e.response?.data?.detail || "Could not post the thank-you."); }
        finally { setThanking(""); }
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name) { toast.error("Name is required."); return; }
        setSaving(true);
        try {
            if (editId) { await axios.put(`${API}/admin/sponsors/${editId}`, form, { headers: h }); toast.success("Sponsor updated!"); }
            else {
                const r = await axios.post(`${API}/admin/sponsors`, form, { headers: h });
                toast.success("Sponsor added!", {
                    duration: 10000,
                    action: { label: "Post a thank-you", onClick: () => postThankYou(r.data) },
                });
            }
            reset(); load();
        } catch { toast.error("Failed to save."); }
        finally { setSaving(false); }
    };

    const edit = (s) => {
        setForm({ name: s.name || "", tier: s.tier || "major", description: s.description || "", website: s.website || "", logo_url: s.logo_url || "", hidden: !!s.hidden });
        setEditId(s.id); setShowForm(true);
    };

    const toggleHide = async (s) => {
        await axios.put(`${API}/admin/sponsors/${s.id}`, { ...s, hidden: !s.hidden }, { headers: h });
        toast.success(s.hidden ? "Sponsor shown." : "Sponsor hidden."); load();
    };

    const del = async (id, name) => {
        if (!window.confirm(`Remove "${name}"?`)) return;
        await axios.delete(`${API}/admin/sponsors/${id}`, { headers: h });
        toast.success("Removed."); load();
    };

    return (
        <div data-testid="sponsors-tab">
            <div className="flex items-center justify-between mb-6">
                <h2 className="font-anton text-2xl text-[#3D2008] uppercase">Sponsors ({sponsors.length})</h2>
                <button onClick={() => { setShowForm(!showForm); reset(); setShowForm(true); }} data-testid="add-sponsor-btn"
                    className="flex items-center gap-2 px-4 py-2 bg-[#C4522A] text-white font-manrope font-semibold text-sm border-2 border-[#3D2008] shadow-[3px_3px_0px_0px_rgba(61,32,8,0.3)] hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-none transition-transform">
                    <Plus size={15} /> Add Sponsor
                </button>
            </div>

            {showForm && (
                <form onSubmit={submit} className="bg-[#F5EDD6] border-2 border-[#3D2008] p-6 mb-6" data-testid="sponsor-form">
                    <h3 className="font-anton text-lg text-[#3D2008] uppercase mb-4">{editId ? "Edit Sponsor" : "New Sponsor"}</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div><label className={labelCls}>Name *</label><input type="text" value={form.name} onChange={f("name")} required placeholder="Business or person" data-testid="sponsor-name-input" className={inputCls} /></div>
                        <div><label className={labelCls}>Tier *</label>
                            <select value={form.tier} onChange={f("tier")} className={inputCls}>
                                {TIER_OPTS.map(t => <option key={t.v} value={t.v}>{t.l}</option>)}
                            </select>
                        </div>
                        <div className="sm:col-span-2"><label className={labelCls}>Description</label><input type="text" value={form.description} onChange={f("description")} placeholder="Short bio or tagline" className={inputCls} /></div>
                        <div><label className={labelCls}>Website</label><input type="text" value={form.website} onChange={f("website")} placeholder="https://..." className={inputCls} /></div>
                        <div><label className={labelCls}>Logo URL</label>
                            <div className="flex items-center gap-2">
                                <input type="text" value={form.logo_url} onChange={f("logo_url")} placeholder="Upload or paste image URL" className={inputCls} />
                                <UploadButton adminKey={adminKey} accept="image/*" onUploaded={(url) => setForm(p => ({ ...p, logo_url: url }))} />
                            </div>
                        </div>
                    </div>
                    <div className="flex gap-3 mt-5">
                        <button type="submit" disabled={saving} data-testid="sponsor-save-btn" className="px-6 py-2 bg-[#C4522A] text-white font-manrope font-semibold text-sm border-2 border-[#3D2008] disabled:opacity-60">{saving ? "Saving..." : editId ? "Update" : "Add Sponsor"}</button>
                        <button type="button" onClick={reset} className="px-6 py-2 bg-white text-[#3D2008] font-manrope font-semibold text-sm border-2 border-[#3D2008]">Cancel</button>
                    </div>
                </form>
            )}

            <div className="space-y-2">
                {sponsors.map(s => (
                    <div key={s.id} className={`bg-white border-2 border-[#3D2008] p-4 flex flex-wrap gap-3 items-center ${s.hidden ? "opacity-50" : ""}`} data-testid={`sponsor-row-${s.id}`}>
                        <LevelBadge level={s.tier} testId={`sponsor-level-${s.id}`} />
                        <div className="flex-1 min-w-[160px]">
                            <p className="font-manrope font-semibold text-[#3D2008]">{s.name} {s.hidden && <span className="text-xs text-[#8A7A65] normal-case">(hidden)</span>}</p>
                            {s.description && <p className="font-manrope text-xs text-[#5C4A35] truncate">{s.description}</p>}
                        </div>
                        <div className="flex items-center gap-2 ml-auto">
                        {s.thank_you_post_id
                            ? <a href={`/whats-happening#post-${s.thank_you_post_id}`} target="_blank" rel="noopener noreferrer" data-testid={`sponsor-thanked-${s.id}`}
                                className="inline-flex items-center gap-1 px-2.5 py-1.5 bg-[#F5EDD6] border border-[#2D5A27] text-[#2D5A27] text-[11px] font-bold uppercase whitespace-nowrap hover:bg-[#2D5A27] hover:text-white transition-colors flex-shrink-0">
                                <Megaphone size={12} /> Thanked
                            </a>
                            : <button onClick={() => postThankYou(s)} disabled={thanking === s.id} title="Post a thank-you to What's Happening" data-testid={`sponsor-thank-btn-${s.id}`}
                                className="inline-flex items-center gap-1 px-2.5 py-1.5 bg-[#C9A84C] border border-[#3D2008] text-[#3D2008] text-[11px] font-bold uppercase whitespace-nowrap hover:bg-[#3D2008] hover:text-[#F5EDD6] transition-colors flex-shrink-0 disabled:opacity-60">
                                <Megaphone size={12} /> {thanking === s.id ? "Posting…" : "Post a thank-you"}
                            </button>}
                        <button onClick={() => toggleHide(s)} title={s.hidden ? "Show" : "Hide"} data-testid={`sponsor-hide-btn-${s.id}`} className="p-2 border border-[#4A7FA5] text-[#4A7FA5] hover:bg-[#4A7FA5] hover:text-white transition-colors flex-shrink-0">{s.hidden ? <Eye size={13} /> : <EyeOff size={13} />}</button>
                        <button onClick={() => edit(s)} data-testid={`sponsor-edit-btn-${s.id}`} className="p-2 border border-[#2D5A27] text-[#2D5A27] hover:bg-[#2D5A27] hover:text-white transition-colors flex-shrink-0"><Edit2 size={13} /></button>
                        <button onClick={() => del(s.id, s.name)} data-testid={`sponsor-delete-btn-${s.id}`} className="p-2 border border-[#C4522A] text-[#C4522A] hover:bg-[#C4522A] hover:text-white transition-colors flex-shrink-0"><Trash2 size={13} /></button>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

// ─── Store Products Tab ───────────────────────────────────────────────────────
const EMPTY_PRODUCT = { name: "", price: "", category: "Apparel", image: "", link: "", hidden: false };
const PRODUCT_CATS = ["Apparel", "Hats", "Stickers", "Drinkware", "Accessories", "Other"];

const StoreTab = ({ adminKey, storeEnabled }) => {
    const [products, setProducts] = useState([]);
    const [showForm, setShowForm] = useState(false);
    const [editId, setEditId] = useState(null);
    const [form, setForm] = useState(EMPTY_PRODUCT);
    const [saving, setSaving] = useState(false);
    const [syncing, setSyncing] = useState(false);
    const h = { "x-admin-password": adminKey };
    const f = (k) => (e) => setForm(p => ({ ...p, [k]: e.target.value }));

    const load = useCallback(async () => {
        const r = await axios.get(`${API}/admin/merch-products`, { headers: { "x-admin-password": adminKey } });
        setProducts(r.data);
    }, [adminKey]);
    useEffect(() => { load(); }, [load]);

    const syncFromPrintify = async () => {
        if (syncing) return;
        setSyncing(true);
        const t = toast.loading("Syncing products from Printify…");
        try {
            const r = await axios.post(`${API}/admin/printify/sync-products`, {}, { headers: h });
            const { added, updated, removed } = r.data;
            toast.success(`Synced! ${added} added, ${updated} updated, ${removed} removed.`, { id: t });
            load();
        } catch {
            toast.error("Sync failed. Check the Printify token/scopes.", { id: t });
        } finally { setSyncing(false); }
    };

    const reset = () => { setForm(EMPTY_PRODUCT); setEditId(null); setShowForm(false); };

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.price) { toast.error("Name and price are required."); return; }
        setSaving(true);
        try {
            if (editId) { await axios.put(`${API}/admin/merch-products/${editId}`, form, { headers: h }); toast.success("Product updated!"); }
            else { await axios.post(`${API}/admin/merch-products`, form, { headers: h }); toast.success("Product added!"); }
            reset(); load();
        } catch { toast.error("Failed to save."); }
        finally { setSaving(false); }
    };

    const edit = (p) => {
        setForm({ name: p.name || "", price: p.price || "", category: p.category || "Apparel", image: p.image || "", link: p.link || "", hidden: !!p.hidden });
        setEditId(p.id); setShowForm(true);
    };

    const toggleHide = async (p) => {
        await axios.put(`${API}/admin/merch-products/${p.id}`, { ...p, hidden: !p.hidden }, { headers: h });
        toast.success(p.hidden ? "Product shown." : "Product hidden."); load();
    };

    const del = async (id, name) => {
        if (!window.confirm(`Delete "${name}"?`)) return;
        await axios.delete(`${API}/admin/merch-products/${id}`, { headers: h });
        toast.success("Deleted."); load();
    };

    return (
        <div data-testid="store-tab">
            <div className="flex items-center justify-between mb-2">
                <h2 className="font-anton text-2xl text-[#3D2008] uppercase">Store Products ({products.length})</h2>
                <div className="flex items-center gap-2">
                    <button onClick={syncFromPrintify} disabled={syncing} data-testid="sync-printify-btn"
                        className="flex items-center gap-2 px-4 py-2 bg-[#2D5A27] text-white font-manrope font-semibold text-sm border-2 border-[#3D2008] shadow-[3px_3px_0px_0px_rgba(61,32,8,0.3)] hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-none transition-transform disabled:opacity-60">
                        <RefreshCw size={15} className={syncing ? "animate-spin" : ""} /> {syncing ? "Syncing…" : "Sync from Printify"}
                    </button>
                    <button onClick={() => { reset(); setShowForm(true); }} data-testid="add-product-btn"
                        className="flex items-center gap-2 px-4 py-2 bg-[#C4522A] text-white font-manrope font-semibold text-sm border-2 border-[#3D2008] shadow-[3px_3px_0px_0px_rgba(61,32,8,0.3)] hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-none transition-transform">
                        <Plus size={15} /> Add Product
                    </button>
                </div>
            </div>
            <p className="font-manrope text-sm text-[#5C4A35] mb-6 max-w-2xl">
                {storeEnabled
                    ? "The store is LIVE on the website. Manage product cards here — the Buy button links to your Printify product page."
                    : "The store is currently HIDDEN from the website. Turn it on in Site Settings when you're ready. You can still edit products here."}
                {" "}<span className="text-[#2D5A27] font-semibold">Sync from Printify</span> pulls your live catalog (adds new, updates changed, removes deleted) while keeping your hidden toggles and any manually-added items.
            </p>

            {showForm && (
                <form onSubmit={submit} className="bg-[#F5EDD6] border-2 border-[#3D2008] p-6 mb-6" data-testid="product-form">
                    <h3 className="font-anton text-lg text-[#3D2008] uppercase mb-4">{editId ? "Edit Product" : "New Product"}</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div><label className={labelCls}>Name *</label><input type="text" value={form.name} onChange={f("name")} required placeholder="Hoodie" data-testid="product-name-input" className={inputCls} /></div>
                        <div><label className={labelCls}>Price * <span className="font-normal normal-case text-gray-400">(e.g. $24.99)</span></label><input type="text" value={form.price} onChange={f("price")} required placeholder="$24.99" data-testid="product-price-input" className={inputCls} /></div>
                        <div><label className={labelCls}>Category *</label>
                            <select value={form.category} onChange={f("category")} className={inputCls}>
                                {PRODUCT_CATS.map(c => <option key={c} value={c}>{c}</option>)}
                            </select>
                        </div>
                        <div><label className={labelCls}>Buy Link (Printify URL)</label><input type="text" value={form.link} onChange={f("link")} placeholder="https://...printify.me/product/..." className={inputCls} /></div>
                        <div className="sm:col-span-2"><label className={labelCls}>Image URL</label>
                            <div className="flex items-center gap-2">
                                <input type="text" value={form.image} onChange={f("image")} placeholder="Upload or paste image URL" className={inputCls} />
                                <UploadButton adminKey={adminKey} accept="image/*" onUploaded={(url) => setForm(p => ({ ...p, image: url }))} />
                            </div>
                        </div>
                    </div>
                    <div className="flex gap-3 mt-5">
                        <button type="submit" disabled={saving} data-testid="product-save-btn" className="px-6 py-2 bg-[#C4522A] text-white font-manrope font-semibold text-sm border-2 border-[#3D2008] disabled:opacity-60">{saving ? "Saving..." : editId ? "Update" : "Add Product"}</button>
                        <button type="button" onClick={reset} className="px-6 py-2 bg-white text-[#3D2008] font-manrope font-semibold text-sm border-2 border-[#3D2008]">Cancel</button>
                    </div>
                </form>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {products.map(p => (
                    <div key={p.id} className={`bg-white border-2 border-[#3D2008] p-3 flex gap-3 items-center ${p.hidden ? "opacity-50" : ""}`} data-testid={`product-row-${p.id}`}>
                        {p.image
                            ? <img src={p.image} alt={p.name} className="w-14 h-14 object-cover border border-[#3D2008] flex-shrink-0" />
                            : <div className="w-14 h-14 bg-[#F5EDD6] border border-[#3D2008] flex-shrink-0" />}
                        <div className="flex-1 min-w-0">
                            <p className="font-manrope font-semibold text-[#3D2008] text-sm truncate">{p.name} {p.hidden && <span className="text-xs text-[#8A7A65] normal-case">(hidden)</span>}</p>
                            <p className="font-manrope text-xs text-[#5C4A35]">{p.category} · {p.price}</p>
                        </div>
                        <div className="flex flex-col gap-1.5 flex-shrink-0">
                            <button onClick={() => toggleHide(p)} title={p.hidden ? "Show" : "Hide"} className="p-1.5 border border-[#4A7FA5] text-[#4A7FA5] hover:bg-[#4A7FA5] hover:text-white transition-colors">{p.hidden ? <Eye size={12} /> : <EyeOff size={12} />}</button>
                            <button onClick={() => edit(p)} className="p-1.5 border border-[#2D5A27] text-[#2D5A27] hover:bg-[#2D5A27] hover:text-white transition-colors"><Edit2 size={12} /></button>
                            <button onClick={() => del(p.id, p.name)} className="p-1.5 border border-[#C4522A] text-[#C4522A] hover:bg-[#C4522A] hover:text-white transition-colors"><Trash2 size={12} /></button>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

// ─── Site Settings Tab ────────────────────────────────────────────────────────
const Toggle = ({ on, onClick, testId }) => (
    <button type="button" onClick={onClick} data-testid={testId}
        className={`relative w-14 h-7 rounded-full border-2 border-[#3D2008] transition-colors duration-200 flex-shrink-0 ${on ? "bg-[#2D5A27]" : "bg-gray-300"}`}>
        <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full transition-transform duration-200 ${on ? "translate-x-7" : "translate-x-0.5"}`} />
    </button>
);

const SettingsTab = ({ adminKey, onChanged }) => {
    const [form, setForm] = useState(null);
    const [saving, setSaving] = useState(false);
    const h = { "x-admin-password": adminKey };
    const f = (k) => (e) => setForm(p => ({ ...p, [k]: e.target.value }));

    useEffect(() => {
        axios.get(`${API}/settings`).then(r => setForm(r.data)).catch(() => {});
    }, []);

    const save = async () => {
        setSaving(true);
        try {
            const r = await axios.put(`${API}/admin/settings`, form, { headers: h });
            setForm(r.data); toast.success("Settings saved!");
            if (onChanged) onChanged(r.data);
        } catch { toast.error("Failed to save."); }
        finally { setSaving(false); }
    };

    if (!form) return <p className="font-manrope text-[#5C4A35]">Loading settings…</p>;

    return (
        <div data-testid="settings-tab" className="max-w-2xl">
            <h2 className="font-anton text-2xl text-[#3D2008] uppercase mb-6">Site Settings</h2>

            {/* Store toggle */}
            <div className="bg-[#F5EDD6] border-2 border-[#3D2008] p-6 mb-6">
                <div className="flex items-center justify-between gap-4">
                    <div>
                        <h3 className="font-anton text-lg text-[#3D2008] uppercase">Merch Store</h3>
                        <p className="font-manrope text-sm text-[#5C4A35] mt-1">Show or hide the Shop on the public website and navigation.</p>
                    </div>
                    <Toggle on={form.store_enabled} onClick={() => setForm(p => ({ ...p, store_enabled: !p.store_enabled }))} testId="settings-store-toggle" />
                </div>
                <p className="font-manrope text-xs font-bold uppercase tracking-wide mt-3" style={{ color: form.store_enabled ? "#2D5A27" : "#C4522A" }}>
                    {form.store_enabled ? "Store is LIVE" : "Store is HIDDEN"}
                </p>
            </div>

            {/* Winning ticket banner */}
            <div className="bg-[#F5EDD6] border-2 border-[#3D2008] p-6 mb-6">
                <div className="flex items-center justify-between gap-4 mb-4">
                    <div>
                        <h3 className="font-anton text-lg text-[#3D2008] uppercase">Winning Ticket Banner</h3>
                        <p className="font-manrope text-sm text-[#5C4A35] mt-1">Show a banner at the top of the Home page to announce a winning ticket number.</p>
                    </div>
                    <Toggle on={form.ticket_banner_enabled} onClick={() => setForm(p => ({ ...p, ticket_banner_enabled: !p.ticket_banner_enabled }))} testId="settings-ticket-toggle" />
                </div>
                <div className="space-y-4">
                    <div><label className={labelCls}>Header</label><input type="text" value={form.ticket_header || ""} onChange={f("ticket_header")} data-testid="ticket-header-input" className={inputCls} /></div>
                    <div><label className={labelCls}>Body Text</label><textarea value={form.ticket_body || ""} onChange={f("ticket_body")} rows={2} className={`${inputCls} resize-none`} /></div>
                    <div><label className={labelCls}>Ticket Number</label><input type="text" value={form.ticket_number || ""} onChange={f("ticket_number")} placeholder="e.g. 042 or Coming Soon" data-testid="ticket-number-input" className={inputCls} /></div>
                </div>
            </div>

            <button onClick={save} disabled={saving} data-testid="settings-save-btn"
                className="flex items-center gap-2 px-6 py-2.5 bg-[#2D5A27] text-white font-manrope font-semibold text-sm border-2 border-[#3D2008] shadow-[3px_3px_0px_0px_rgba(61,32,8,0.3)] hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-none transition-transform disabled:opacity-60">
                {saving ? "Saving..." : "Save Settings"}
            </button>
        </div>
    );
};

// ─── Contacts Tab (read full message + reply) ─────────────────────────────────
const ContactsTab = ({ contacts }) => {
    const [selected, setSelected] = useState(null);
    const fmtd = (d) => d ? new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—";
    return (
        <div data-testid="contacts-tab">
            <h2 className="font-anton text-2xl text-[#3D2008] uppercase mb-6">Contacts ({contacts.length})</h2>
            {contacts.length === 0
                ? <p className="text-center py-12 text-gray-400 font-manrope border-2 border-[#3D2008]">No contacts yet.</p>
                : <div className="overflow-x-auto border-2 border-[#3D2008]">
                    <table className="w-full border-collapse text-sm">
                        <thead><tr className="bg-[#3D2008] text-[#F5EDD6]">
                            {["Date", "Name", "Email", "Phone", "Message", ""].map(h => (
                                <th key={h} className="text-left px-4 py-3 font-manrope font-bold uppercase text-xs tracking-wide whitespace-nowrap">{h}</th>
                            ))}
                        </tr></thead>
                        <tbody>
                            {contacts.map((c, i) => (
                                <tr key={c.created_at || i} className={i % 2 === 0 ? "bg-white" : "bg-[#F5EDD6]/60"}>
                                    <td className="px-4 py-3 font-manrope text-[#3D2008] border-b border-gray-100 whitespace-nowrap">{fmtd(c.created_at)}</td>
                                    <td className="px-4 py-3 font-manrope text-[#3D2008] border-b border-gray-100">{c.name || "—"}</td>
                                    <td className="px-4 py-3 border-b border-gray-100">{c.email ? <a href={`mailto:${c.email}`} className="text-[#C4522A] underline font-manrope">{c.email}</a> : "—"}</td>
                                    <td className="px-4 py-3 font-manrope text-[#3D2008] border-b border-gray-100">{c.phone || "—"}</td>
                                    <td className="px-4 py-3 border-b border-gray-100 align-top"><ExpandableText text={c.message} testId={`contact-message-${i}`} /></td>
                                    <td className="px-4 py-3 border-b border-gray-100">
                                        <button onClick={() => setSelected(c)} data-testid={`contact-open-${i}`}
                                            className="flex items-center gap-1.5 px-3 py-1.5 bg-[#3D2008] text-[#F5EDD6] font-manrope font-semibold text-xs hover:bg-[#C4522A] transition-colors whitespace-nowrap">
                                            <Mail size={13} /> Open
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>}

            {selected && (
                <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => setSelected(null)} data-testid="contact-modal">
                    <div className="bg-white border-2 border-[#3D2008] max-w-lg w-full p-6" onClick={e => e.stopPropagation()}>
                        <div className="flex justify-between items-start mb-4">
                            <div>
                                <h3 className="font-anton text-xl text-[#3D2008] uppercase">{selected.name || "Contact"}</h3>
                                <p className="font-manrope text-sm text-[#5C4A35]">{fmtd(selected.created_at)}</p>
                            </div>
                            <button onClick={() => setSelected(null)} data-testid="contact-modal-close" className="text-[#3D2008] hover:text-[#C4522A]"><X size={22} /></button>
                        </div>
                        <div className="space-y-1.5 mb-4 font-manrope text-sm">
                            <p><span className="font-bold text-[#3D2008]">Email:</span> {selected.email ? <a href={`mailto:${selected.email}`} className="text-[#C4522A] underline">{selected.email}</a> : "—"}</p>
                            <p><span className="font-bold text-[#3D2008]">Phone:</span> {selected.phone ? <a href={`tel:${selected.phone}`} className="text-[#C4522A] underline">{selected.phone}</a> : "—"}</p>
                        </div>
                        <div className="bg-[#F5EDD6] border border-[#D5D0C7] p-4 mb-5 max-h-64 overflow-y-auto">
                            <p className="font-manrope text-[#3D2008] whitespace-pre-wrap break-words">{selected.message || "(no message)"}</p>
                        </div>
                        {selected.email && (
                            <a href={`mailto:${selected.email}?subject=${encodeURIComponent("Re: Your message to Seeley Lake Skatepark")}&body=${encodeURIComponent("Hi " + (selected.name || "") + ",\n\n")}`}
                                data-testid="contact-reply-btn"
                                className="inline-flex items-center gap-2 px-6 py-2.5 bg-[#C4522A] text-white font-manrope font-semibold text-sm border-2 border-[#3D2008] shadow-[3px_3px_0px_0px_rgba(61,32,8,0.3)] hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-none transition-transform">
                                <Mail size={15} /> Reply via Email
                            </a>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

// ─── Gallery Tab (upload / delete photos) ─────────────────────────────────────
const GalleryTab = ({ adminKey }) => {
    const [photos, setPhotos] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [caption, setCaption] = useState("");
    const [tall, setTall] = useState(false);
    const fileRef = useRef(null);
    const h = { "x-admin-password": adminKey };

    const load = useCallback(async () => {
        const r = await axios.get(`${API}/admin/gallery`, { headers: { "x-admin-password": adminKey } });
        setPhotos(r.data);
    }, [adminKey]);
    useEffect(() => { load(); }, [load]);

    const src = (img) => (img.image || "").startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${img.image}` : img.image;

    const onUpload = async (e) => {
        const file = e.target.files && e.target.files[0];
        if (!file) return;
        setUploading(true);
        const fd = new FormData();
        fd.append("file", file);
        fd.append("caption", caption);
        fd.append("tall", tall ? "true" : "false");
        try {
            await axios.post(`${API}/admin/gallery`, fd, { headers: { ...h, "Content-Type": "multipart/form-data" } });
            toast.success("Photo added!");
            setCaption(""); setTall(false);
            if (fileRef.current) fileRef.current.value = "";
            load();
        } catch { toast.error("Upload failed. Use a jpg/png/webp under 15MB."); }
        finally { setUploading(false); }
    };

    const del = async (id) => {
        if (!window.confirm("Delete this photo from the website?")) return;
        await axios.delete(`${API}/admin/gallery/${id}`, { headers: h });
        toast.success("Photo deleted."); load();
    };

    return (
        <div data-testid="gallery-tab">
            <h2 className="font-anton text-2xl text-[#3D2008] uppercase mb-2">Photos ({photos.length})</h2>
            <p className="font-manrope text-sm text-[#5C4A35] mb-6 max-w-2xl">Add or remove photos shown on the public Gallery page. Changes appear immediately.</p>
            <div className="bg-[#F5EDD6] border-2 border-[#3D2008] p-6 mb-6">
                <h3 className="font-anton text-lg text-[#3D2008] uppercase mb-4 flex items-center gap-2"><Upload size={18} /> Add a Photo</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                    <div><label className={labelCls}>Caption (optional)</label><input type="text" value={caption} onChange={e => setCaption(e.target.value)} placeholder="e.g. Community build day" data-testid="gallery-caption-input" className={inputCls} /></div>
                    <label className="flex items-center gap-2 sm:mt-7 font-manrope text-sm text-[#3D2008]">
                        <input type="checkbox" checked={tall} onChange={e => setTall(e.target.checked)} data-testid="gallery-tall-checkbox" /> Tall image (portrait orientation)
                    </label>
                </div>
                <input ref={fileRef} type="file" accept="image/*" onChange={onUpload} disabled={uploading} data-testid="gallery-file-input"
                    className="block w-full text-sm font-manrope file:mr-4 file:py-2 file:px-4 file:border-2 file:border-[#3D2008] file:bg-[#C4522A] file:text-white file:font-semibold file:cursor-pointer" />
                {uploading && <p className="font-manrope text-sm text-[#2D5A27] mt-3">Uploading…</p>}
            </div>
            {photos.length === 0
                ? <p className="font-manrope text-center text-[#8A7A65] py-12">No photos yet.</p>
                : <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                    {photos.map(img => (
                        <div key={img.id} className="relative border-2 border-[#3D2008] group" data-testid={`gallery-admin-${img.id}`}>
                            <img src={src(img)} alt={img.alt} className="w-full h-40 object-cover" />
                            <button onClick={() => del(img.id)} data-testid={`gallery-delete-${img.id}`}
                                className="absolute top-1 right-1 p-1.5 bg-[#C4522A] text-white border border-[#3D2008] opacity-90 hover:opacity-100 transition-opacity"><Trash2 size={13} /></button>
                            {img.caption && <div className="absolute bottom-0 left-0 right-0 bg-[#3D2008]/80 px-2 py-1"><span className="font-manrope text-xs text-[#F5EDD6]">{img.caption}</span></div>}
                        </div>
                    ))}
                </div>}
        </div>
    );
};

// ─── Documents Tab (upload PDFs/images for public download) ───────────────────
const DocumentsTab = ({ adminKey }) => {
    const [docs, setDocs] = useState([]);
    const [title, setTitle] = useState("");
    const [description, setDescription] = useState("");
    const [busy, setBusy] = useState(false);
    const ref = useRef(null);
    const h = { "x-admin-password": adminKey };
    const load = useCallback(async () => { const r = await axios.get(`${API}/documents`); setDocs(r.data); }, []);
    useEffect(() => { load(); }, [load]);
    const pick = async (e) => {
        const file = e.target.files && e.target.files[0];
        if (!file) return;
        if (!title.trim()) { toast.error("Enter a title first."); if (ref.current) ref.current.value = ""; return; }
        setBusy(true);
        const fd = new FormData(); fd.append("file", file); fd.append("title", title); fd.append("description", description);
        try { await axios.post(`${API}/admin/documents`, fd, { headers: { ...h, "Content-Type": "multipart/form-data" } }); toast.success("Document added!"); setTitle(""); setDescription(""); load(); }
        catch { toast.error("Upload failed (PDF or image, max 25MB)."); }
        finally { setBusy(false); if (ref.current) ref.current.value = ""; }
    };
    const del = async (id) => { if (!window.confirm("Delete this document?")) return; await axios.delete(`${API}/admin/documents/${id}`, { headers: h }); toast.success("Deleted."); load(); };
    return (
        <div data-testid="documents-tab">
            <h2 className="font-anton text-2xl text-[#3D2008] uppercase mb-2">Documents ({docs.length})</h2>
            <p className="font-manrope text-sm text-[#5C4A35] mb-6 max-w-2xl">Upload PDFs (receipts, 501(c)(3) letter, sponsorship packet). These appear as downloads on the public site.</p>
            <div className="bg-[#F5EDD6] border-2 border-[#3D2008] p-6 mb-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                    <div><label className={labelCls}>Title *</label><input type="text" value={title} onChange={e => setTitle(e.target.value)} placeholder="Sponsorship Packet" data-testid="doc-title-input" className={inputCls} /></div>
                    <div><label className={labelCls}>Description</label><input type="text" value={description} onChange={e => setDescription(e.target.value)} placeholder="Short description" className={inputCls} /></div>
                </div>
                <input ref={ref} type="file" accept=".pdf,image/*" onChange={pick} disabled={busy} data-testid="doc-file-input"
                    className="block w-full text-sm font-manrope file:mr-4 file:py-2 file:px-4 file:border-2 file:border-[#3D2008] file:bg-[#C4522A] file:text-white file:font-semibold file:cursor-pointer" />
                {busy && <p className="font-manrope text-sm text-[#2D5A27] mt-3">Uploading…</p>}
            </div>
            <div className="space-y-2">
                {docs.map(d => (
                    <div key={d.id} className="bg-white border-2 border-[#3D2008] p-4 flex gap-3 items-center" data-testid={`doc-row-${d.id}`}>
                        <FileText size={20} className="text-[#C4522A] flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                            <a href={`${process.env.REACT_APP_BACKEND_URL}${d.url}`} target="_blank" rel="noopener noreferrer" className="font-manrope font-semibold text-[#3D2008] hover:text-[#C4522A]">{d.title}</a>
                            {d.description && <p className="font-manrope text-xs text-[#5C4A35] truncate">{d.description}</p>}
                        </div>
                        <button onClick={() => del(d.id)} className="p-2 border border-[#C4522A] text-[#C4522A] hover:bg-[#C4522A] hover:text-white transition-colors flex-shrink-0"><Trash2 size={13} /></button>
                    </div>
                ))}
            </div>
        </div>
    );
};

// ─── Main Admin Component ─────────────────────────────────────────────────────
const TABS = [
    { id: "dashboard",        label: "Dashboard",        icon: LayoutDashboard },
    { id: "record-donation",  label: "Record Donation",  icon: PlusCircle },
    { id: "events",           label: "Events",           icon: Calendar },
    { id: "sponsors",         label: "Sponsors",         icon: Star },
    { id: "store",            label: "Store Products",   icon: Store },
    { id: "settings",         label: "Site Settings",    icon: Settings },
    { id: "gallery",          label: "Photos",           icon: ImageIcon },
    { id: "documents",        label: "Documents",        icon: FileText },
    { id: "donations",        label: "All Donations",    icon: DollarSign },
    { id: "merch",            label: "Merch Orders",     icon: ShoppingBag },
    { id: "subscribers",      label: "Email List",       icon: Mail },
    { id: "contacts",         label: "Contacts",         icon: MessageSquare },
    { id: "volunteers",       label: "Volunteers",       icon: Users },
    { id: "posts",            label: "What's Happening", icon: Megaphone },
    { id: "transfer",         label: "Data Transfer",    icon: Database },
];

const Admin = () => {
    const [adminKey, setAdminKey] = useState(() => sessionStorage.getItem(SESSION_KEY) || "");
    const [authed, setAuthed] = useState(false);
    const [tab, setTab] = useState("dashboard");
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [stats, setStats] = useState(null);
    const [donations, setDonations] = useState([]);
    const [contacts, setContacts] = useState([]);
    const [subscribers, setSubscribers] = useState([]);
    const [volunteers, setVolunteers] = useState([]);
    const [merch, setMerch] = useState([]);
    const [siteSettings, setSiteSettings] = useState(null);
    const [sendingReceipt, setSendingReceipt] = useState("");
    const [editingDonation, setEditingDonation] = useState(null);

    const fetchAll = useCallback(async (key) => {
        setLoading(true);
        const h = { "x-admin-password": key };
        const [s, d, c, sub, vol, m] = await Promise.allSettled([
            axios.get(`${API}/admin/stats`, { headers: h }),
            axios.get(`${API}/admin/donations`, { headers: h }),
            axios.get(`${API}/admin/contacts`, { headers: h }),
            axios.get(`${API}/admin/subscribers`, { headers: h }),
            axios.get(`${API}/admin/volunteers`, { headers: h }),
            axios.get(`${API}/admin/merch-orders`, { headers: h }),
        ]);
        if (s.status === "fulfilled") setStats(s.value.data);
        if (d.status === "fulfilled") setDonations(d.value.data);
        if (c.status === "fulfilled") setContacts(c.value.data);
        if (sub.status === "fulfilled") setSubscribers(sub.value.data);
        if (vol.status === "fulfilled") setVolunteers(vol.value.data);
        if (m.status === "fulfilled") setMerch(m.value.data);
        axios.get(`${API}/settings`).then(r => setSiteSettings(r.data)).catch(() => {});
        setLoading(false);
    }, []);

    useEffect(() => {
        const saved = sessionStorage.getItem(SESSION_KEY);
        if (!saved) return;
        axios.get(`${API}/admin/stats`, { headers: { "x-admin-password": saved } })
            .then(() => { setAuthed(true); fetchAll(saved); })
            .catch(() => { sessionStorage.removeItem(SESSION_KEY); setAdminKey(""); });
    }, [fetchAll]);

    const handleLogin = (key) => { setAdminKey(key); setAuthed(true); fetchAll(key); };
    const handleLogout = () => { sessionStorage.removeItem(SESSION_KEY); setAdminKey(""); setAuthed(false); };

    if (!authed) return <AdminLogin onLogin={handleLogin} />;

    const fmt = (dateStr) => dateStr ? new Date(dateStr).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—";
    const trunc = (s, n = 70) => s ? (s.length > n ? s.slice(0, n) + "…" : s) : "—";

    const toggleWall = async (sid) => {
        try {
            const r = await axios.post(`${API}/admin/donations/${sid}/toggle-wall`, {}, { headers: { "x-admin-password": adminKey } });
            toast.success(r.data.shown ? "Added to donor wall" : "Removed from donor wall");
            fetchAll(adminKey);
        } catch { toast.error("Failed to update donor wall."); }
    };

    const deleteDonation = async (d) => {
        if (!confirmDeleteDonation(d)) return;
        try {
            await axios.delete(`${API}/admin/donations/${d.session_id}`, { headers: { "x-admin-password": adminKey } });
            toast.success("Donation removed.");
            fetchAll(adminKey);
        } catch (e) { toast.error(e.response?.data?.detail || "Could not delete."); }
    };

    const sendReceipt = async (d) => {
        if (!window.confirm(`Email a thank-you receipt to ${d.donor_email}?`)) return;
        setSendingReceipt(d.session_id);
        try {
            const r = await axios.post(`${API}/admin/donations/${d.session_id}/send-thank-you`, {}, { headers: { "x-admin-password": adminKey } });
            toast.success(r.data.message);
            fetchAll(adminKey);
        } catch (e) { toast.error(e.response?.data?.detail || "Could not send the email."); }
        finally { setSendingReceipt(""); }
    };

    const renderContent = () => {
        switch (tab) {
            case "dashboard": return (
                <div data-testid="dashboard-tab">
                    <div className="flex items-center justify-between mb-6">
                        <h2 className="font-anton text-2xl text-[#3D2008] uppercase">Dashboard</h2>
                        <button onClick={() => fetchAll(adminKey)} className="flex items-center gap-2 px-3 py-1.5 border-2 border-[#3D2008] font-manrope text-sm text-[#3D2008] hover:bg-[#3D2008] hover:text-white transition-colors">
                            <RefreshCw size={13} /> Refresh
                        </button>
                    </div>
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                        <StatCard label="Total Raised" value={`$${(stats?.total_raised || 0).toLocaleString()}`} color="bg-[#C4522A]" />
                        <StatCard label="Donors" value={stats?.donor_count || 0} color="bg-[#3D2008]" />
                        <StatCard label="Merch Revenue" value={`$${(stats?.merch_revenue || 0).toLocaleString()}`} color="bg-[#2D5A27]" />
                        <StatCard label="Merch Orders" value={stats?.merch_count || 0} color="bg-[#4A7FA5]" />
                        <StatCard label="Subscribers" value={stats?.subscriber_count || 0} color="bg-[#C9A84C]" />
                        <StatCard label="Contacts" value={stats?.contact_count || 0} color="bg-[#3D2008]" />
                        <StatCard label="Volunteers" value={stats?.volunteer_count || 0} color="bg-[#2D5A27]" />
                        <StatCard label="Goal" value="$400,000" color="bg-[#C4522A]" />
                    </div>
                    <div className="bg-[#F5EDD6] border-2 border-[#3D2008] p-6">
                        <p className="font-manrope text-sm text-[#5C4A35] leading-relaxed">
                            Welcome to the SLSA Admin Panel. Use the sidebar to manage events, sponsors, and view all submissions. Changes to events and sponsors are live on the website immediately.
                        </p>
                    </div>
                </div>
            );
            case "record-donation": return <RecordDonationTab adminKey={adminKey} onRefresh={() => fetchAll(adminKey)} />;
            case "events": return <EventsTab adminKey={adminKey} />;
            case "sponsors": return <SponsorsTab adminKey={adminKey} />;
            case "store": return <StoreTab adminKey={adminKey} storeEnabled={!!siteSettings?.store_enabled} />;
            case "settings": return <SettingsTab adminKey={adminKey} onChanged={(s) => setSiteSettings(s)} />;
            case "donations": return (
                <div>
                    {editingDonation && <EditDonationModal donation={editingDonation} adminKey={adminKey} onClose={() => setEditingDonation(null)}
                        onSaved={() => { setEditingDonation(null); fetchAll(adminKey); }} />}
                    <h2 className="font-anton text-2xl text-[#3D2008] uppercase mb-6">All Donations ({donations.length})</h2>
                    <div className="overflow-x-auto border-2 border-[#3D2008]">
                        {donations.length === 0
                            ? <p className="text-center py-12 text-gray-400 font-manrope">No donations yet.</p>
                            : <table className="w-full border-collapse text-sm">
                                <thead><tr className="bg-[#3D2008] text-[#F5EDD6]">
                                    {["Date","Name","Email","Amount","Method","Note","Status","Wall","Receipt","Actions"].map(h => (
                                        <th key={h} className="text-left px-4 py-3 font-manrope font-bold uppercase text-xs tracking-wide whitespace-nowrap">{h}</th>
                                    ))}
                                </tr></thead>
                                <tbody>
                                    {donations.map((d, i) => (
                                        <tr key={d.session_id || i} className={i % 2 === 0 ? "bg-white" : "bg-[#F5EDD6]/60"}>
                                            <td className="px-4 py-3 font-manrope text-[#3D2008] border-b border-gray-100">{d.created_at ? new Date(d.created_at).toLocaleDateString() : "—"}</td>
                                            <td className="px-4 py-3 font-manrope text-[#3D2008] border-b border-gray-100">{d.donor_name || "—"}</td>
                                            <td className="px-4 py-3 font-manrope text-[#3D2008] border-b border-gray-100">{d.donor_email || "—"}</td>
                                            <td className="px-4 py-3 font-manrope text-[#3D2008] border-b border-gray-100 font-semibold">${d.amount || 0}</td>
                                            <td className="px-4 py-3 border-b border-gray-100">
                                                {d.package_id === "monthly" ? (
                                                    <span className="inline-flex items-center gap-1 text-xs font-bold px-2 py-0.5 uppercase bg-[#C9A84C] text-[#3D2008]" data-testid={`monthly-badge-${i}`}>
                                                        <Repeat size={11} /> Monthly
                                                    </span>
                                                ) : (
                                                    <span className={`text-xs font-bold px-2 py-0.5 uppercase ${METHOD_BADGE[d.method || d.package_id] || "bg-gray-200 text-[#3D2008]"}`}>
                                                        {d.method || d.package_id || "stripe"}
                                                    </span>
                                                )}
                                            </td>
                                            <td className="px-4 py-3 border-b border-gray-100 align-top"><ExpandableText text={d.note} testId={`donation-note-${i}`} /></td>
                                            <td className="px-4 py-3 border-b border-gray-100">
                                                <span className={`text-xs font-bold px-2 py-0.5 ${d.payment_status === "paid" ? "bg-[#2D5A27] text-white" : "bg-gray-300 text-[#3D2008]"}`}>
                                                    {d.payment_status || "—"}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3 border-b border-gray-100">
                                                {(d.donor_name || "").trim() ? (() => {
                                                    const shown = d.wall_override ? d.wall_override === "show" : (d.amount || 0) >= 250;
                                                    return (
                                                        <button onClick={() => toggleWall(d.session_id)} data-testid={`wall-toggle-${i}`}
                                                            className={`flex items-center gap-1 text-xs font-bold px-2 py-1 border transition-colors whitespace-nowrap ${shown ? "border-[#2D5A27] text-[#2D5A27] hover:bg-[#2D5A27] hover:text-white" : "border-gray-400 text-gray-500 hover:bg-gray-100"}`}>
                                                            {shown ? <><Eye size={12} /> On Wall</> : <><EyeOff size={12} /> Off Wall</>}
                                                        </button>
                                                    );
                                                })() : <span className="text-gray-300 text-xs">—</span>}
                                            </td>
                                            <td className="px-4 py-3 border-b border-gray-100">
                                                {(d.donor_email || "").trim() ? (
                                                    <div className="flex flex-col items-start gap-0.5">
                                                        <button onClick={() => sendReceipt(d)} disabled={sendingReceipt === d.session_id} data-testid={`send-receipt-${i}`}
                                                            className={`flex items-center gap-1 text-xs font-bold px-2 py-1 border transition-colors whitespace-nowrap disabled:opacity-60 ${d.thank_you_sent_at ? "border-gray-400 text-gray-500 hover:bg-gray-100" : "border-[#C4522A] text-[#C4522A] hover:bg-[#C4522A] hover:text-white"}`}>
                                                            <Mail size={12} /> {sendingReceipt === d.session_id ? "Sending…" : d.thank_you_sent_at ? "Re-send" : "Send receipt"}
                                                        </button>
                                                        {d.thank_you_sent_at && <span className="text-[10px] text-[#2D5A27] font-semibold whitespace-nowrap" data-testid={`receipt-sent-${i}`}>Sent {new Date(d.thank_you_sent_at).toLocaleDateString()}</span>}
                                                    </div>
                                                ) : <span className="text-gray-300 text-xs">no email</span>}
                                            </td>
                                            <td className="px-4 py-3 border-b border-gray-100">
                                                <div className="flex flex-wrap gap-1.5">
                                                    <button onClick={() => setEditingDonation(d)} data-testid={`donation-edit-${i}`}
                                                        className="inline-flex items-center gap-1 text-xs font-bold px-2 py-1 border border-[#3D2008] text-[#3D2008] hover:bg-[#3D2008] hover:text-white whitespace-nowrap"><Pencil size={11} /> Edit</button>
                                                    <button onClick={() => deleteDonation(d)} data-testid={`donation-delete-${i}`}
                                                        className="inline-flex items-center gap-1 text-xs font-bold px-2 py-1 border border-red-700 text-red-700 hover:bg-red-700 hover:text-white whitespace-nowrap"><Trash2 size={11} /> Delete</button>
                                                </div>
                                                {d.edited_at && <span className="block text-[10px] text-[#8A7A65] mt-1 whitespace-nowrap" data-testid={`donation-edited-${i}`}>edited {new Date(d.edited_at).toLocaleDateString()}</span>}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        }
                    </div>
                </div>
            );
            case "merch": return (
                <div>
                    <h2 className="font-anton text-2xl text-[#3D2008] uppercase mb-6">Merch Orders ({merch.length})</h2>
                    <DataTable
                        headers={["Date", "Customer", "Email", "Total", "Status", "Printful ID"]}
                        rows={merch.map(o => [fmt(o.created_at), o.customer_name || "—", o.customer_email || "—", `$${o.total || 0}`, o.status || "—", o.printful_order_id || "pending"])}
                        empty="No merch orders yet."
                    />
                </div>
            );
            case "subscribers": return (
                <div>
                    <h2 className="font-anton text-2xl text-[#3D2008] uppercase mb-6">Email List ({subscribers.length})</h2>
                    <DataTable
                        headers={["Date", "Name", "Email"]}
                        rows={subscribers.map(s => [fmt(s.created_at), s.name || "—", s.email || "—"])}
                        empty="No subscribers yet."
                    />
                </div>
            );
            case "contacts": return <ContactsTab contacts={contacts} />;
            case "gallery": return <GalleryTab adminKey={adminKey} />;
            case "documents": return <DocumentsTab adminKey={adminKey} />;
            case "posts": return <PostsTab adminKey={adminKey} />;
            case "transfer": return <DataTransferTab adminKey={adminKey} onImported={() => fetchAll(adminKey)} />;
            case "volunteers": return (
                <div>
                    <h2 className="font-anton text-2xl text-[#3D2008] uppercase mb-6">Volunteers ({volunteers.length})</h2>
                    <DataTable
                        headers={["Date", "Name", "Email", "Phone", "Interests"]}
                        rows={volunteers.map(v => [fmt(v.created_at), v.name || "—", v.email || "—", v.phone || "—", trunc(v.interests, 50)])}
                        empty="No volunteers yet."
                    />
                </div>
            );
            default: return null;
        }
    };

    return (
        <div className="min-h-screen bg-gray-100 font-manrope">
            {/* Top bar */}
            <div className="bg-[#3D2008] border-b-2 border-[#C9A84C]/50 px-4 py-3 flex items-center justify-between sticky top-0 z-40">
                <div className="flex items-center gap-3">
                    <button onClick={() => setSidebarOpen(!sidebarOpen)} data-testid="admin-mobile-menu-btn" aria-label="Toggle menu" className="lg:hidden p-1.5 text-[#F5EDD6] hover:text-[#C4522A] transition-colors">
                        {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
                    </button>
                    <div className="font-anton text-[#F5EDD6] uppercase tracking-wide text-lg">SLSA <span className="text-[#C9A84C] font-manrope font-normal normal-case text-sm tracking-normal">Admin</span></div>
                </div>
                <div className="flex items-center gap-4">
                    {loading && <RefreshCw size={14} className="text-[#C9A84C] animate-spin" />}
                    <a href="/" target="_blank" rel="noopener noreferrer" className="font-manrope text-[#F5EDD6]/50 text-xs hover:text-[#F5EDD6] transition-colors hidden sm:block">View Site ↗</a>
                    <button onClick={handleLogout} data-testid="admin-logout-btn"
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-[#C4522A] text-white font-manrope text-xs font-semibold border border-[#C9A84C] hover:bg-[#C4522A]/80 transition-colors">
                        <LogOut size={12} /> Logout
                    </button>
                </div>
            </div>

            <div className="flex relative">
                {/* Mobile overlay */}
                {sidebarOpen && <div className="fixed inset-0 bg-black/50 z-20 lg:hidden" onClick={() => setSidebarOpen(false)} />}

                {/* Sidebar */}
                <aside className={`${sidebarOpen ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0 fixed lg:sticky top-[57px] h-[calc(100vh-57px)] w-52 bg-[#3D2008] border-r border-[#C9A84C]/20 z-30 transition-transform duration-200 overflow-y-auto flex-shrink-0`}>
                    <nav className="p-3 space-y-0.5 pt-4">
                        {TABS.map(({ id, label, icon: Icon }) => (
                            <button key={id} onClick={() => { setTab(id); setSidebarOpen(false); }}
                                data-testid={`admin-tab-${id}`}
                                className={`w-full flex items-center gap-3 px-3 py-2.5 text-left font-manrope text-sm rounded-none transition-colors ${tab === id ? "bg-[#C4522A] text-white" : "text-[#F5EDD6]/60 hover:text-[#F5EDD6] hover:bg-white/10"}`}>
                                <Icon size={15} /> {label}
                            </button>
                        ))}
                    </nav>
                </aside>

                {/* Main content */}
                <main className="flex-1 p-6 lg:p-8 min-w-0 overflow-x-auto">
                    {renderContent()}
                </main>
            </div>
        </div>
    );
};

export default Admin;
